/**
 * Recover from a tab that is older than the deployment.
 *
 * The site is split into chunks that load when a route is first opened, each
 * with a hash in its filename. A deploy publishes new filenames and removes the
 * old ones — so a tab left open across a deploy asks for a chunk that no longer
 * exists, gets a 404, and React throws ChunkLoadError. Nothing caught it, so the
 * screen simply stopped working, with an error only visible in the console.
 *
 * On 21 August 2026 that happened on the live site after several deploys in one
 * afternoon: a browser holding main.c5a4c43f.js asked for 7.1df41979.chunk.js,
 * which had been replaced. Anybody with the site open when we deploy hits this,
 * staff and customers alike, and the honest answer to "the page broke" is that
 * they are running half of an old version.
 *
 * The fix is to reload once, which fetches the current index.html and with it
 * the current filenames. Once, guarded by a flag in sessionStorage: if the
 * reload does not resolve it the fault is something else, and reloading forever
 * would be worse than the original error.
 */

const FLAG = "clinton:reloaded-for-stale-build";

const isStaleBuildError = (value) => {
  const name = value?.name || "";
  const message = String(value?.message || value || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk \S+ failed/i.test(message) ||
    /Loading CSS chunk \S+ failed/i.test(message) ||
    // Safari's wording when a module script 404s
    /Importing a module script failed/i.test(message)
  );
};

const recover = (reason) => {
  if (sessionStorage.getItem(FLAG)) {
    // Already tried. Leave the error alone so it can be seen and reported.
    return false;
  }
  sessionStorage.setItem(FLAG, String(Date.now()));
  // eslint-disable-next-line no-console
  console.warn(`Reloading once: this tab is older than the deployment (${reason}).`);
  window.location.reload();
  return true;
};

export default function watchForStaleBuild() {
  // A chunk that fails inside React's rendering surfaces here.
  window.addEventListener("error", (event) => {
    if (isStaleBuildError(event.error) || isStaleBuildError(event.message)) {
      recover("a script chunk was missing");
    }
  });

  // A dynamic import that rejects surfaces here instead.
  window.addEventListener("unhandledrejection", (event) => {
    if (isStaleBuildError(event.reason)) {
      recover("a chunk failed to load");
    }
  });

  /*
   * Clear the flag once a page has loaded successfully, so a genuine stale-build
   * error weeks later is still recovered from. Without this the guard is
   * permanent for the life of the tab's session storage.
   */
  window.addEventListener("load", () => {
    setTimeout(() => sessionStorage.removeItem(FLAG), 5000);
  });
}
